'use client';

/**
 * WebHowItWorksCard — section headline + numbered-step list.
 *
 * Ports klyde-website/src/pages/ForBuyers.tsx how-it-works section.
 * Each step renders with a circled index + a single line of prose.
 * Content via `web_for_buyers_how_it_works` strategy.
 *
 * @layer presentation
 * @arch_component_type widget
 * @arch_domain canvas
 */

import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Loader2, XCircle } from 'lucide-react';
import { useCapabilityStore } from '@/lib/stores/capability-store';
import { api } from '@/lib/api/client';
import { useCardTypography } from '@/lib/design/card-chrome';
import { cn } from '@/lib/utils/cn';
import type { WebHowItWorksContent } from '@/lib/api/types';
import { useLanguageStore } from '@/lib/stores/language-store';


interface WebHowItWorksCardProps {
  /** Capability action — defaults to for-buyers; landing variant
   *  passes 'web_landing_how_it_works'. */
  strategyAction?: string;
}

export function WebHowItWorksCard({
  strategyAction = 'web_for_buyers_how_it_works',
}: WebHowItWorksCardProps = {}) {
  const T = useCardTypography();
  const strategy = useCapabilityStore(
    (s) => s.strategyName('content', strategyAction),
  );
  const lang = useLanguageStore((s) => s.lang) ?? undefined;

  const [data, setData] = useState<WebHowItWorksContent | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!strategy) return;
    let cancelled = false;
    api
      .getStrategyData<WebHowItWorksContent>(strategy, lang ? { lang } : {})
      .then((res) => {
        if (cancelled) return;
        if (!res.success) {
          setError(res.error || 'Failed to load steps');
          return;
        }
        setData(res.data ?? null);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message ?? 'Network error');
      });
    return () => { cancelled = true; };
  }, [strategy, lang]);

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-destructive">
        <XCircle className="h-6 w-6 mb-2" />
        <p className={T.body}>{error}</p>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex items-center justify-center py-24 text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin mr-2" />
        Loading…
      </div>
    );
  }

  return (
    <section className="web-surface relative py-24 px-6 bg-background text-foreground overflow-hidden">
      <div className="container mx-auto max-w-3xl">
        <motion.h2
          className={cn(T.name, 'text-center mb-16')}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          {data.headline}
          {data.headline_emphasis && (
            <>
              {' '}
              <span className="text-primary">{data.headline_emphasis}</span>
            </>
          )}
        </motion.h2>

        <div className="space-y-6">
          {data.steps.map((step, i) => (
            <motion.div
              key={i}
              className="flex gap-5 items-start"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 + i * 0.12, duration: 0.5 }}
            >
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                <span className="text-primary font-bold text-sm">{i + 1}</span>
              </div>
              <div className="pt-1">
                {step.title && (
                  <h3 className="text-lg font-bold mb-1 text-foreground">{step.title}</h3>
                )}
                <p className={cn(T.body, 'text-base text-foreground')}>
                  {step.body}
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
